import React from 'react';
import request from 'arcdynamic-request';
import Dropdown from "../Dropdown/Dropdown";
import PropTypes from "prop-types";
import filter from "../../filter";
import { executeQuery } from '../../requests/executeQuery';
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { AGENTS_LIST, AGENTS_LIST_COUNT } from '../../consts/queries';
import { AGENTS, TEAM } from '../../consts/tables';
import { getData } from '../../requests/CRUD';
import { formatName } from "../../utils";
import "./SelectAgent.scss";

const cloneDeep = require('lodash.clonedeep');
const limit = 10;
const debounceMs = 250;

// The dropdown's own "Add new" shows only when its filter finds nobody, and a
// box searching the server skips that filter, so the box ends its list with this.
const ADD_NEW_OPTION = { addNewAgent: true, NAME: 'Add new agent' };

class SelectAgent extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            items: [],
            currentOffset: 0,
            hasMore: true,
            isLoading: false,
            searchTerm: "",
            inputValue: "",
            selectedOptions: [],
        };

        this.debounceTimer = null;
        this.activeRequestId = 0;

        this.fetchPage = this.fetchPage.bind(this);
        this.startSearch = this.startSearch.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSelectedOptionsChange = this.handleSelectedOptionsChange.bind(this);
        this.syncSelectedOptionsFromValue = this.syncSelectedOptionsFromValue.bind(this);
        this.ensureSelectedOption = this.ensureSelectedOption.bind(this);
        this.getOptionLabel = this.getOptionLabel.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.renderOption = this.renderOption.bind(this);
    }

    componentDidMount() {
        this.startSearch("");
        this.ensureSelectedOption(this.props.value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.newAgent !== prevProps.newAgent && this.props.newAgent) {
            const items = cloneDeep(this.state.items);
            items.push(this.props.newAgent);
            // The box that added the agent drops what was typed, so it shows the agent.
            const addedHere = String(this.props.value) === String(this.props.newAgent.ARCIMEDES_ROW_ID);
            this.setState(addedHere ? { items, searchTerm: '' } : { items }, this.syncSelectedOptionsFromValue);
        }

        if (prevProps.value !== this.props.value) {
            this.ensureSelectedOption(this.props.value);
        }

        if (prevProps.value !== this.props.value || prevState.items !== this.state.items) {
            this.syncSelectedOptionsFromValue();
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        this.activeRequestId++;
    }

    extractId(option) {
        if (!option) return null;
        if (typeof option === 'object') {
            if (option.ARCIMEDES_ROW_ID !== undefined) return String(option.ARCIMEDES_ROW_ID);
            if (option.value !== undefined) return String(option.value);
        }
        return option !== undefined && option !== null ? String(option) : null;
    }

    getSelectedIdsFromValue() {
        const { value } = this.props;
        if (value === null || value === undefined) return [];
        if (Array.isArray(value)) return value.map((v) => String(this.extractId(v))).filter(Boolean);
        if (typeof value === 'object') {
            const id = this.extractId(value);
            return id ? [id] : [];
        }
        return String(value).split(',').map(v => v.trim()).filter(Boolean);
    }

    syncSelectedOptionsFromValue() {
        const selectedIds = this.getSelectedIdsFromValue();
        if (!selectedIds.length) {
            if (this.state.selectedOptions.length) {
                this.setState({ selectedOptions: [] });
            }
            return;
        }

        const map = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        this.state.items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && selectedIds.includes(id)) {
                map.set(id, opt);
            }
        });

        const next = selectedIds.map(id => map.get(id)).filter(Boolean);
        const nextState = { selectedOptions: next };
        if (!this.state.searchTerm && next.length === 1) {
            const label = this.getOptionLabel(next[0]);
            if (label && this.state.inputValue !== label) {
                nextState.inputValue = label;
            }
        }
        this.setState(nextState);
    }

    ensureSelectedOption(value) {
        const id = this.extractId(value);
        if (!id) return;

        const alreadyThere = this.state.items.find(item => this.extractId(item) === id);
        if (alreadyThere) return;

        const { typeKey, data: parentData } = this.props;
        const refType = typeKey && parentData ? parentData[typeKey] : null;

        const pushRow = (row, groupedByForTypeKey) => {
            const withGroup =
                typeKey && row && groupedByForTypeKey != null
                    ? { ...row, groupedBy: groupedByForTypeKey }
                    : row;
            this.setState(
                (prev) => ({
                    items: [withGroup, ...prev.items],
                }),
                this.syncSelectedOptionsFromValue
            );
        };

        getData({
            tableCode: AGENTS,
            options: {
                limit: { count: 1, offset: 0 },
                column: [
                    'ARCIMEDES_ROW_ID',
                    'FIRST_NAME',
                    'LAST_NAME',
                    'FIRM',
                    'DELETED',
                ],
                filter: [
                    {
                        code: 'ARCIMEDES_ROW_ID',
                        text: id,
                        type: 'equal',
                    }
                ],
                filterType: 'and',
            },
        })
            .then((res) => {
                if (res && res.success && Array.isArray(res.data) && res.data.length) {
                    pushRow(res.data[0], typeKey ? 'oa' : undefined);
                    return;
                }
                if (refType === 'oa') return;

                const tryTeam =
                    refType === 'active' ||
                    refType === 'inactive' ||
                    refType == null ||
                    refType === '';

                if (!tryTeam) return;

                return getData({
                    tableCode: TEAM,
                    options: {
                        limit: { count: 1, offset: 0 },
                        column: [
                            'ARCIMEDES_ROW_ID',
                            'FIRST_NAME',
                            'LAST_NAME',
                            'DELETED',
                        ],
                        filter: [
                            {
                                code: 'ARCIMEDES_ROW_ID',
                                text: id,
                                type: 'equal',
                            },
                        ],
                        filterType: 'and',
                    },
                }).then((teamRes) => {
                    if (
                        teamRes &&
                        teamRes.success &&
                        Array.isArray(teamRes.data) &&
                        teamRes.data.length
                    ) {
                        const row = teamRes.data[0];
                        const gb = row.DELETED === 'y' ? 'inactive' : 'active';
                        pushRow(row, typeKey ? gb : undefined);
                    }
                });
            })
            .catch(() => {});
    }

    fetchPage({ offset, searchTerm, replace }) {
        const requestId = ++this.activeRequestId;
        const trimmedTerm = (searchTerm || "").trim();

        const options = filter({
            NAME: trimmedTerm,
        }, "agents");
        if (!trimmedTerm) {
            options.param.NAME = '';
        }

        options.param.limit = limit;
        options.param.offset = offset;

        this.setState({ isLoading: true });

        return Promise.all([
            executeBackendQuery({ queryName: AGENTS_LIST, options }),
            executeBackendQuery({ queryName: AGENTS_LIST_COUNT, options }),
        ])
            .then((responses) => {
                if (requestId !== this.activeRequestId) return;

                const listRes = responses && responses[0] ? responses[0] : null;
                const countRes = responses && responses[1] ? responses[1] : null;

                if (listRes && listRes.success && Array.isArray(listRes.data)) {
                    const withGroupedBy = (row) =>
                        this.props.typeKey ? { ...row, groupedBy: 'oa' } : row;
                    const newItems = listRes.data
                        .filter(item => item && (item.FIRST_NAME || item.LAST_NAME))
                        .map(withGroupedBy);
                    const totalCount = countRes && countRes.success && countRes.data && countRes.data[0]
                        ? Number(countRes.data[0].TOTAL_COUNT)
                        : null;
                    this.setState((prev) => ({
                        items: replace ? newItems : [...prev.items, ...newItems],
                        currentOffset: offset + newItems.length,
                        hasMore: totalCount === null ? (newItems.length === limit) : (offset + newItems.length < totalCount),
                        isLoading: false,
                        searchTerm,
                    }));
                } else {
                    this.setState({ isLoading: false, hasMore: false });
                }
            })
            .catch((err) => {
                if (requestId !== this.activeRequestId) return;
                console.error("Error fetching agents:", err);
                this.setState({ isLoading: false });
            });
    }

    startSearch(searchTerm) {
        this.setState({
            items: [],
            currentOffset: 0,
            hasMore: true,
            searchTerm,
        });
        return this.fetchPage({ offset: 0, searchTerm, replace: true });
    }

    handleInputChange(event, newValue, reason) {
        if (reason === "reset") return;
        if (reason === "clear") {
            this.setState({ inputValue: "" });
            this.startSearch("");
            return;
        }

        const next = (newValue || "").trimStart();
        this.setState({ inputValue: next });

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.startSearch(next);
        }, debounceMs);
    }

    handleLoadMore() {
        if (!this.state.isLoading && this.state.hasMore) {
            this.fetchPage({
                offset: this.state.currentOffset,
                searchTerm: this.state.searchTerm,
                replace: false,
            });
        }
    }

    getOptionLabel(option) {
        if (!option || typeof option !== 'object') return '';
        return formatName(option) || '';
    }

    handleSelectedOptionsChange(options) {
        if (options && options.addNewAgent) return;

        const nextOptions = Array.isArray(options) ? options.filter(Boolean) : (options ? [options] : []);
        const map = new Map();
        nextOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        const selectedOptions = Array.from(map.values());
        const nextState = { selectedOptions };
        if (selectedOptions.length === 1) {
            const label = this.getOptionLabel(selectedOptions[0]);
            if (label) {
                nextState.inputValue = label;
                nextState.searchTerm = "";
            }
        }
        if (selectedOptions.length === 0) {
            nextState.inputValue = "";
            nextState.searchTerm = "";
        }
        this.setState(nextState);
    }

    handleChange(...args) {
        const picked = args[5];

        if (picked && picked.addNewAgent) {
            this.props.addNewItem(this.props.valueKey);
            return;
        }

        this.props.handleParentChange(...args);
    }

    /** The label the dropdown gives an agent, and the Add new row in the Firm box's style. */
    renderOption(option) {
        if (option.addNewAgent) {
            return <span className="SelectAgent_add-new">+ Add new agent</span>;
        }

        const name = formatName(option) || '';
        const firmName = option.FIRM_NAME && option.FIRM_NAME.trim();

        return firmName ? `${name} - ${firmName}` : name;
    }

    render() {
        const { items, hasMore, isLoading, inputValue } = this.state;
        const {
            theme,
            value,
            valueKey,
            label,
            disabled,
            data,
            withAddNew,
            handleParentChange,
            handleInputParentChange,
            isFilter,
            viewMode,
            addNewItem,
            deleted,
            hasAgentPercent,
            isFromProperty,
            typeKey,
            ...rest
        } = this.props;

        const mergedOptionsMap = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) mergedOptionsMap.set(id, opt);
        });
        items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && !mergedOptionsMap.has(id)) mergedOptionsMap.set(id, opt);
        });
        const mergedOptions = Array.from(mergedOptionsMap.values());
        const canAddNew = Boolean(withAddNew && addNewItem && !viewMode);

        return (
            <Dropdown theme={theme}
                      optionsType='agent'
                      optionKey='ARCIMEDES_ROW_ID'
                      options={canAddNew ? [...mergedOptions, ADD_NEW_OPTION] : mergedOptions}
                      value={value}
                      valueKey={valueKey}
                      typeKey={typeKey}
                      typeValue={typeKey && data ? data[typeKey] : undefined}
                      label={label}
                      disabled={disabled}
                      data={data}
                      isFilter={isFilter}
                      handleParentChange={canAddNew ? this.handleChange : handleParentChange}
                      handleInputParentChange={handleInputParentChange}
                      withAddNew={withAddNew}
                      viewMode={viewMode}
                      deleted={deleted}
                      addNewItem={addNewItem}
                      hasAgentPercent={hasAgentPercent}
                      isFromProperty={isFromProperty}
                      disableClientFilter={true}
                      onInputChange={this.handleInputChange}
                      inputValue={inputValue}
                      loading={isLoading}
                      loadingText="Searching..."
                      noOptionsText={inputValue ? "No matching agents" : "Type to search agents"}
                      onSelectedOptionsChange={this.handleSelectedOptionsChange}
                      renderOption={canAddNew ? this.renderOption : undefined}
                      ListboxProps={{
                          onScroll: (e) => {
                              const { target } = e;
                              if (target.scrollTop + target.clientHeight >= target.scrollHeight * 0.8) {
                                  if (hasMore && !isLoading) {
                                      this.handleLoadMore();
                                  }
                              }
                          }
                      }}
                      {...rest}
            />
        )
    }
}

SelectAgent.propTypes = {
    theme: PropTypes.oneOf(['default', 'white', 'dark']),
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    typeKey: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    data: PropTypes.object,
    withAddNew: PropTypes.bool,
    handleParentChange: PropTypes.func.isRequired,
    handleInputParentChange: PropTypes.func,
    isFilter: PropTypes.bool,
    viewMode: PropTypes.bool,
    addNewItem: PropTypes.func,
    deleted: PropTypes.bool,
    hasAgentPercent: PropTypes.bool,
    isFromProperty: PropTypes.bool,
};

export default SelectAgent;
